/**
 * Observability Contract
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 3 (Observability)
 *
 * Defines interfaces for LLM observability using Langfuse:
 * - Trace creation and management
 * - Span/Generation tracking
 * - Evaluation metrics
 * - Session and user tracking
 *
 * @see https://langfuse.com/docs
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export enum ObservabilityProvider {
  LANGFUSE = 'LANGFUSE',
  OPENTELEMETRY = 'OPENTELEMETRY',
  CUSTOM = 'CUSTOM',
}

export enum TraceLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum SpanType {
  GENERATION = 'GENERATION', // LLM call
  RETRIEVAL = 'RETRIEVAL', // RAG retrieval
  TOOL = 'TOOL', // Tool execution
  CHAIN = 'CHAIN', // Chain/workflow step
  AGENT = 'AGENT', // Agent orchestration
  GUARDRAIL = 'GUARDRAIL', // Guardrail check
  EMBEDDING = 'EMBEDDING', // Embedding generation
  EVALUATION = 'EVALUATION', // Evaluation span
}

// ============================================================================
// LANGFUSE TYPES
// ============================================================================

/**
 * Langfuse trace configuration
 */
export interface LangfuseTraceConfig {
  name: string;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
  tags?: string[];
  release?: string;
  version?: string;
  public?: boolean;
}

/**
 * Langfuse span/generation input
 */
export interface LangfuseSpanInput {
  traceId: string;
  name: string;
  type: SpanType;
  input?: unknown;
  output?: unknown;
  metadata?: Record<string, unknown>;
  level?: TraceLevel;
  statusMessage?: string;
  startTime?: Date;
  endTime?: Date;
  parentSpanId?: string;
}

/**
 * Langfuse generation input (for LLM calls)
 */
export interface LangfuseGenerationInput {
  traceId: string;
  name: string;
  model: string;
  modelParameters?: {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
    [key: string]: unknown;
  };
  input?: unknown;
  output?: unknown;
  usage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  metadata?: Record<string, unknown>;
  level?: TraceLevel;
  statusMessage?: string;
  startTime?: Date;
  endTime?: Date;
  parentSpanId?: string;
  completionStartTime?: Date;
}

/**
 * Langfuse score input (for evaluation metrics)
 */
export interface LangfuseScoreInput {
  traceId: string;
  name: string;
  value: number;
  comment?: string;
  observationId?: string;
  dataType?: 'NUMERIC' | 'BOOLEAN' | 'CATEGORICAL';
}

/**
 * Langfuse event input
 */
export interface LangfuseEventInput {
  traceId: string;
  name: string;
  input?: unknown;
  output?: unknown;
  metadata?: Record<string, unknown>;
  level?: TraceLevel;
  startTime?: Date;
  parentSpanId?: string;
}

// ============================================================================
// SERVICE INTERFACES
// ============================================================================

/**
 * Trace result
 */
export interface TraceResult {
  traceId: string;
  url?: string;
  createdAt: Date;
}

/**
 * Span result
 */
export interface SpanResult {
  spanId: string;
  traceId: string;
  parentSpanId?: string;
}

/**
 * Generation result
 */
export interface GenerationResult {
  generationId: string;
  traceId: string;
  parentSpanId?: string;
}

/**
 * Create trace request
 */
export interface CreateTraceRequest {
  businessId: string;
  config: LangfuseTraceConfig;
}

/**
 * Add span request
 */
export interface AddSpanRequest {
  businessId: string;
  span: LangfuseSpanInput;
}

/**
 * Add generation request
 */
export interface AddGenerationRequest {
  businessId: string;
  generation: LangfuseGenerationInput;
}

/**
 * Add score request
 */
export interface AddScoreRequest {
  businessId: string;
  score: LangfuseScoreInput;
}

/**
 * Flush request
 */
export interface FlushRequest {
  businessId: string;
  traceId?: string;
}

// ============================================================================
// OBSERVABILITY CONFIG
// ============================================================================

export interface LangfuseConfig {
  enabled: boolean;
  publicKey: string;
  secretKey: string;
  baseUrl?: string;
  flushAt?: number;
  flushInterval?: number;
  release?: string;
}

export interface ObservabilityConfig {
  provider: ObservabilityProvider;
  langfuse?: LangfuseConfig;
  enabledMetrics: string[];
  sampleRate: number; // 0.0 to 1.0
  debugMode: boolean;
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const langfuseTraceConfigSchema = z.object({
  name: z.string().min(1, 'Trace name is required'),
  userId: z.string().optional(),
  sessionId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  release: z.string().optional(),
  version: z.string().optional(),
  public: z.boolean().optional(),
});

export const langfuseSpanInputSchema = z.object({
  traceId: z.string().min(1, 'Trace ID is required'),
  name: z.string().min(1, 'Span name is required'),
  type: z.nativeEnum(SpanType),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  level: z.nativeEnum(TraceLevel).optional(),
  statusMessage: z.string().optional(),
  startTime: z.date().optional(),
  endTime: z.date().optional(),
  parentSpanId: z.string().optional(),
});

export const langfuseGenerationInputSchema = z.object({
  traceId: z.string().min(1, 'Trace ID is required'),
  name: z.string().min(1, 'Generation name is required'),
  model: z.string().min(1, 'Model is required'),
  modelParameters: z
    .object({
      temperature: z.number().optional(),
      maxTokens: z.number().optional(),
      topP: z.number().optional(),
      frequencyPenalty: z.number().optional(),
      presencePenalty: z.number().optional(),
    })
    .passthrough()
    .optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  usage: z
    .object({
      promptTokens: z.number().optional(),
      completionTokens: z.number().optional(),
      totalTokens: z.number().optional(),
    })
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  level: z.nativeEnum(TraceLevel).optional(),
  statusMessage: z.string().optional(),
  startTime: z.date().optional(),
  endTime: z.date().optional(),
  parentSpanId: z.string().optional(),
  completionStartTime: z.date().optional(),
});

export const langfuseScoreInputSchema = z.object({
  traceId: z.string().min(1, 'Trace ID is required'),
  name: z.string().min(1, 'Score name is required'),
  value: z.number(),
  comment: z.string().optional(),
  observationId: z.string().optional(),
  dataType: z.enum(['NUMERIC', 'BOOLEAN', 'CATEGORICAL']).optional(),
});

export const createTraceRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  config: langfuseTraceConfigSchema,
});

export const addSpanRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  span: langfuseSpanInputSchema,
});

export const addGenerationRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  generation: langfuseGenerationInputSchema,
});

export const addScoreRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  score: langfuseScoreInputSchema,
});

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

export const DEFAULT_OBSERVABILITY_CONFIG: ObservabilityConfig = {
  provider: ObservabilityProvider.LANGFUSE,
  enabledMetrics: ['latency', 'tokens', 'cost', 'quality'],
  sampleRate: 1.0,
  debugMode: false,
};
